import React, { useState, useEffect, useCallback, useRef } from 'react';
import { resolveMediaUrl } from '../../utils/mediaUrl';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const MODE_CARDS = [
  {
    id: 'auto',
    label: 'Ajánlott',
    desc: 'Automatikus válogatás a legjobb képekből',
    icon: '★',
  },
  {
    id: 'lastMonth',
    label: 'Elmúlt hónap',
    desc: 'Az elmúlt hónap legjobb képei',
    icon: '📅',
  },
  {
    id: 'random',
    label: 'Véletlenszerű',
    desc: 'Véletlenszerűen választott képek',
    icon: '⤮',
  },
  {
    id: 'manual',
    label: 'Kézi válogatás',
    desc: 'Ön választja ki a képeket a galériából',
    icon: '▦',
  },
];

const dateFormatter = new Intl.DateTimeFormat('hu-HU', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function emptySettings() {
  return {
    mode: 'auto',
    slideLimit: 8,
    sortOrder: 'newest',
    selectedPostIds: [],
    excludedPostIds: [],
    pinnedPostIds: [],
    dateRange: null,
  };
}

function modeStatusLabel(mode, dateRange) {
  if (mode === 'lastMonth') return 'elmúlt hónap';
  if (mode === 'random') return 'véletlenszerű';
  if (mode === 'manual') return 'kézi válogatás';
  if (mode === 'dateRange' && dateRange?.fromMonthKey && dateRange?.toMonthKey) {
    return `${dateRange.fromMonthKey} – ${dateRange.toMonthKey}`;
  }
  return 'legújabb';
}

function formatSlideDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return dateFormatter.format(d);
}

function captionTitle(caption, max = 48) {
  const t = (caption || '').trim();
  if (!t) return 'Galéria kép';
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

const HomepageHeroManager = ({ apiCall, showToast }) => {
  const [settings, setSettings] = useState(emptySettings);
  const [previewItems, setPreviewItems] = useState([]);
  const [slideCount, setSlideCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesHasMore, setCandidatesHasMore] = useState(false);
  const [monthSteps, setMonthSteps] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const previewTimerRef = useRef(null);
  const candidatesRef = useRef([]);
  const searchQueryRef = useRef(searchQuery);

  useEffect(() => {
    candidatesRef.current = candidates;
  }, [candidates]);

  useEffect(() => {
    searchQueryRef.current = searchQuery;
  }, [searchQuery]);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall('/hero/config');
      setSettings(data.settings || emptySettings());
      setSlideCount(typeof data.slideCount === 'number' ? data.slideCount : 0);
      setPreviewItems(Array.isArray(data.previewItems) ? data.previewItems : []);
    } catch {
      showToast('Nem sikerült betölteni a főoldal beállításokat.', 'error');
    } finally {
      setLoading(false);
    }
  }, [apiCall, showToast]);

  const fetchPreview = useCallback(
    async (nextSettings) => {
      setPreviewLoading(true);
      try {
        const data = await apiCall('/hero/preview', 'POST', nextSettings);
        setSlideCount(typeof data.slideCount === 'number' ? data.slideCount : 0);
        setPreviewItems(Array.isArray(data.previewItems) ? data.previewItems : []);
      } catch {
        /* keep previous preview */
      } finally {
        setPreviewLoading(false);
      }
    },
    [apiCall]
  );

  const schedulePreview = useCallback(
    (nextSettings) => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
      previewTimerRef.current = setTimeout(() => fetchPreview(nextSettings), 400);
    },
    [fetchPreview]
  );

  const updateSettings = useCallback(
    (updater) => {
      setSettings((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
        schedulePreview(next);
        return next;
      });
    },
    [schedulePreview]
  );

  const loadMonthSteps = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/social-feed/meta`);
      const data = res.ok ? await res.json() : {};
      setMonthSteps(Array.isArray(data.monthSteps) ? data.monthSteps : []);
    } catch {
      setMonthSteps([]);
    }
  }, []);

  const fetchCandidatesPage = useCallback(
    async (offset) => {
      const params = new URLSearchParams({
        offset: String(offset),
        limit: '48',
      });
      const q = searchQueryRef.current.trim();
      if (q) params.set('q', q);
      const data = await apiCall(`/hero/candidates?${params.toString()}`);
      return {
        items: Array.isArray(data.items) ? data.items : [],
        hasMore: Boolean(data.hasMore),
      };
    },
    [apiCall]
  );

  const resetCandidates = useCallback(async () => {
    setCandidatesLoading(true);
    try {
      const { items, hasMore } = await fetchCandidatesPage(0);
      setCandidates(items);
      setCandidatesHasMore(hasMore);
    } catch {
      setCandidates([]);
      setCandidatesHasMore(false);
    } finally {
      setCandidatesLoading(false);
    }
  }, [fetchCandidatesPage]);

  const loadMoreCandidates = useCallback(async () => {
    if (candidatesLoading) return;
    setCandidatesLoading(true);
    try {
      const offset = candidatesRef.current.length;
      const { items, hasMore } = await fetchCandidatesPage(offset);
      setCandidates((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        const merged = [...prev];
        for (const row of items) {
          if (!seen.has(row.id)) {
            seen.add(row.id);
            merged.push(row);
          }
        }
        return merged;
      });
      setCandidatesHasMore(hasMore);
    } catch {
      showToast('Nem sikerült betölteni a további képeket.', 'error');
    } finally {
      setCandidatesLoading(false);
    }
  }, [candidatesLoading, fetchCandidatesPage, showToast]);

  useEffect(() => {
    loadConfig();
    loadMonthSteps();
    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, [loadConfig, loadMonthSteps]);

  useEffect(() => {
    if (settings.mode !== 'manual') return undefined;
    const t = setTimeout(() => resetCandidates(), 300);
    return () => clearTimeout(t);
  }, [settings.mode, searchQuery, resetCandidates]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const data = await apiCall('/hero/config', 'PUT', settings);
      setSettings(data.settings || settings);
      setSlideCount(typeof data.slideCount === 'number' ? data.slideCount : 0);
      setPreviewItems(Array.isArray(data.previewItems) ? data.previewItems : []);
      showToast('Főoldal főképek mentve.', 'success');
    } catch {
      showToast('Mentés sikertelen.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const selectMode = (mode) => {
    updateSettings((s) => ({
      ...s,
      mode,
      excludedPostIds: s.mode === mode ? s.excludedPostIds || [] : [],
      pinnedPostIds: s.mode === mode ? s.pinnedPostIds || [] : [],
    }));
  };

  const toggleSelected = (id) => {
    updateSettings((prev) => {
      const ids = prev.selectedPostIds || [];
      const has = ids.includes(id);
      const selectedPostIds = has ? ids.filter((x) => x !== id) : [...ids, id];
      return { ...prev, mode: 'manual', selectedPostIds };
    });
  };

  const replacePreviewSlide = useCallback(
    async (id) => {
      const index = previewItems.findIndex((i) => i.id === id);
      if (index < 0) return;
      const keepIds = previewItems.filter((i) => i.id !== id).map((i) => i.id);
      const excluded = [...new Set([...(settings.excludedPostIds || []), id])];
      const nextSettings = { ...settings, excludedPostIds: excluded };

      setPreviewLoading(true);
      try {
        const data = await apiCall('/hero/replace-slide', 'POST', {
          settings: nextSettings,
          keepIds,
          removedId: id,
          replaceIndex: index,
        });
        if (data.settings) setSettings(data.settings);
        setSlideCount(typeof data.slideCount === 'number' ? data.slideCount : keepIds.length);
        setPreviewItems(Array.isArray(data.previewItems) ? data.previewItems : []);
      } catch {
        showToast('Nem sikerült lecserélni a képet.', 'error');
      } finally {
        setPreviewLoading(false);
      }
    },
    [apiCall, previewItems, settings, showToast]
  );

  const removePreviewItem = (id) => {
    if (settings.mode === 'manual') {
      updateSettings((prev) => ({
        ...prev,
        selectedPostIds: (prev.selectedPostIds || []).filter((x) => x !== id),
      }));
      return;
    }
    replacePreviewSlide(id);
  };

  const reorderIds = useCallback((ids, fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return null;
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(toId);
    if (fromIdx < 0 || toIdx < 0) return null;
    const next = [...ids];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, fromId);
    return next;
  }, []);

  const reorderPreview = useCallback(
    (fromId, toId) => {
      if (settings.mode === 'manual') {
        updateSettings((prev) => {
          const next = reorderIds(prev.selectedPostIds || [], fromId, toId);
          return next ? { ...prev, selectedPostIds: next } : prev;
        });
        return;
      }

      const currentIds = previewItems.map((i) => i.id);
      const orderedIds = reorderIds(currentIds, fromId, toId);
      if (!orderedIds) return;

      const byId = new Map(previewItems.map((i) => [i.id, i]));
      setPreviewItems(orderedIds.map((id) => byId.get(id)).filter(Boolean));
      setSettings((prev) => ({ ...prev, pinnedPostIds: orderedIds }));
    },
    [previewItems, reorderIds, settings.mode, updateSettings]
  );

  const handleDragStart = (id) => setDragId(id);
  const handleDragEnd = () => {
    setDragId(null);
    setDragOverId(null);
  };
  const handleDrop = (targetId) => {
    if (dragId) reorderPreview(dragId, targetId);
    setDragId(null);
    setDragOverId(null);
  };

  const dateRange = settings.dateRange || { fromMonthKey: '', toMonthKey: '' };
  const sortLabel = settings.sortOrder === 'random' ? 'véletlenszerű' : 'legújabb';
  const showSortToggles = settings.mode === 'auto' || settings.mode === 'lastMonth' || settings.mode === 'dateRange';

  if (loading) {
    return <p className="hero-admin-loading">Betöltés…</p>;
  }

  return (
    <div className="hero-admin">
      <header className="hero-admin-header">
        <div>
          <h2 className="hero-admin-title">Kezdőlap főképek kezelése</h2>
          <p className="hero-admin-subtitle">
            Itt állíthatja be, mely képek jelenjenek meg a kezdőlap tetején váltakozva.
          </p>
        </div>
        <div className="hero-admin-header-actions">
          <a href="/" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
            👁 Előnézet
          </a>
          <button type="button" className="btn btn-primary" onClick={saveSettings} disabled={saving}>
            {saving ? 'Mentés…' : '💾 Mentés'}
          </button>
        </div>
      </header>

      <div className="hero-admin-status-bar">
        <span className="hero-admin-stat">
          <strong>{slideCount}</strong> kép aktív
        </span>
        <span className="hero-admin-stat-divider" aria-hidden="true" />
        <span className="hero-admin-stat">Forrás: közösségi galéria</span>
        <span className="hero-admin-stat-divider" aria-hidden="true" />
        <span className="hero-admin-stat">Sorrend: {sortLabel}</span>
        <span className="hero-admin-stat-divider" aria-hidden="true" />
        <span className="hero-admin-stat">
          Időszak: {modeStatusLabel(settings.mode, settings.dateRange)}
        </span>
        {previewLoading && <span className="hero-admin-stat hero-admin-stat--muted">Frissítés…</span>}
      </div>

      <div className="hero-admin-mode-grid">
        {MODE_CARDS.map((card) => (
          <button
            key={card.id}
            type="button"
            className={`hero-admin-mode-card${settings.mode === card.id ? ' is-active' : ''}`}
            onClick={() => selectMode(card.id)}
          >
            <span className="hero-admin-mode-card-icon" aria-hidden="true">
              {card.icon}
            </span>
            <span className="hero-admin-mode-card-label">{card.label}</span>
            <span className="hero-admin-mode-card-desc">{card.desc}</span>
            {settings.mode === card.id && (
              <span className="hero-admin-mode-card-check" aria-hidden="true">
                ✓
              </span>
            )}
          </button>
        ))}
      </div>

      <p className="hero-admin-date-link-wrap">
        <button
          type="button"
          className="hero-admin-date-link"
          onClick={() => selectMode('dateRange')}
        >
          Egyéni időszak megadása (hónaptól hónapig)…
        </button>
      </p>

      <div className="hero-admin-controls-row">
        <div className="hero-admin-control-block">
          <label className="hero-admin-control-label" htmlFor="hero-slide-count">
            Hány kép váltakozzon?
          </label>
          <div className="hero-admin-count-row">
            <input
              id="hero-slide-count"
              type="number"
              className="hero-admin-count-input"
              min={3}
              max={20}
              value={settings.slideLimit}
              onChange={(e) =>
                updateSettings({
                  slideLimit: Math.min(20, Math.max(3, parseInt(e.target.value, 10) || 8)),
                  pinnedPostIds: [],
                })
              }
            />
            <input
              type="range"
              className="hero-admin-range"
              min={3}
              max={20}
              value={settings.slideLimit}
              onChange={(e) =>
                updateSettings({ slideLimit: parseInt(e.target.value, 10), pinnedPostIds: [] })
              }
              aria-label="Képek száma csúszkával"
            />
          </div>
          <p className="hero-admin-control-hint">Ajánlott: 6–10 kép</p>
        </div>

        {showSortToggles && (
          <div className="hero-admin-control-block">
            <span className="hero-admin-control-label">Rendezési beállítások</span>
            <div className="hero-admin-sort-toggles">
              <button
                type="button"
                className={`hero-admin-sort-btn${settings.sortOrder === 'newest' ? ' is-active' : ''}`}
                onClick={() => updateSettings({ sortOrder: 'newest', pinnedPostIds: [] })}
              >
                Legújabb elől
              </button>
              <button
                type="button"
                className={`hero-admin-sort-btn${settings.sortOrder === 'random' ? ' is-active' : ''}`}
                onClick={() => updateSettings({ sortOrder: 'random', pinnedPostIds: [] })}
              >
                Véletlenszerű sorrend
              </button>
            </div>
          </div>
        )}
      </div>

      {settings.mode === 'dateRange' && (
        <div className="hero-admin-daterange-panel">
          <span className="hero-admin-control-label">Időszak (hónapok)</span>
          <div className="hero-admin-daterange-inputs">
            <input
              type="month"
              value={dateRange.fromMonthKey || ''}
              onChange={(e) =>
                updateSettings({
                  dateRange: { ...dateRange, fromMonthKey: e.target.value },
                  pinnedPostIds: [],
                })
              }
              list="hero-month-list"
              aria-label="Kezdő hónap"
            />
            <span className="hero-admin-daterange-sep">–</span>
            <input
              type="month"
              value={dateRange.toMonthKey || ''}
              onChange={(e) =>
                updateSettings({
                  dateRange: { ...dateRange, toMonthKey: e.target.value },
                  pinnedPostIds: [],
                })
              }
              list="hero-month-list"
              aria-label="Záró hónap"
            />
          </div>
          <datalist id="hero-month-list">
            {monthSteps.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
        </div>
      )}

      <div className="hero-admin-main">
        <div className="hero-admin-main-col">
          <h3 className="hero-admin-section-title">Jelenleg megjelenő főképek</h3>

          {settings.mode === 'manual' && (
            <div className="hero-admin-manual-pick">
              <input
                type="search"
                placeholder="Keresés a galériában…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="hero-admin-search"
              />
              <div className="hero-admin-candidate-grid">
                {candidates.map((item) => {
                  const selected = (settings.selectedPostIds || []).includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`hero-admin-candidate${selected ? ' is-selected' : ''}`}
                      onClick={() => toggleSelected(item.id)}
                      title={captionTitle(item.caption, 80)}
                    >
                      <img src={resolveMediaUrl(item.imageUrl)} alt="" loading="lazy" />
                      {selected && <span className="hero-admin-candidate-check">✓</span>}
                    </button>
                  );
                })}
              </div>
              {candidatesLoading && <p className="hero-admin-muted">Képek betöltése…</p>}
              {!candidatesLoading && candidatesHasMore && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={loadMoreCandidates}
                >
                  További képek
                </button>
              )}
            </div>
          )}

          {previewItems.length > 0 ? (
            <div className="hero-admin-preview-grid">
              {previewItems.map((item) => (
                <div
                  key={item.id}
                  className={`hero-admin-preview-card${dragId === item.id ? ' is-dragging' : ''}${dragOverId === item.id && dragId !== item.id ? ' is-drag-over' : ''}`}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragId && dragId !== item.id) setDragOverId(item.id);
                  }}
                  onDragLeave={() => {
                    if (dragOverId === item.id) setDragOverId(null);
                  }}
                  onDrop={() => handleDrop(item.id)}
                >
                  <span
                    className="hero-admin-preview-drag"
                    draggable
                    onDragStart={(e) => {
                      e.stopPropagation();
                      handleDragStart(item.id);
                    }}
                    aria-hidden="true"
                    title="Húzza a sorrend módosításához"
                  >
                    ⠿
                  </span>
                  <button
                    type="button"
                    className="hero-admin-preview-remove"
                    onClick={() => removePreviewItem(item.id)}
                    aria-label="Eltávolítás és másik kép"
                    title={
                      settings.mode === 'manual'
                        ? 'Eltávolítás a listából'
                        : 'Másik kép betöltése helyette'
                    }
                  >
                    ×
                  </button>
                  <img src={resolveMediaUrl(item.imageUrl)} alt="" loading="lazy" />
                  <span className="hero-admin-preview-badge">Aktív</span>
                  <div className="hero-admin-preview-meta">
                    <span className="hero-admin-preview-date">{formatSlideDate(item.publishedAt)}</span>
                    <span className="hero-admin-preview-caption">{captionTitle(item.caption)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="hero-admin-muted">Nincs megjeleníthető kép ezekkel a beállításokkal.</p>
          )}

          {previewItems.length > 0 && (
            <p className="hero-admin-drag-hint">
              A ⠿ ikonnal húzással állíthatja a sorrendet.
              {settings.mode === 'manual'
                ? ' Mentés után lép életbe a főoldalon.'
                : ' A × gombbal egy képet kicserélhet — a többi marad. Mentés után érvényes a főoldalon is.'}
            </p>
          )}
        </div>

        <aside className="hero-admin-tips">
          <h3 className="hero-admin-tips-title">Tippek a főképekhez</h3>
          <ul className="hero-admin-tips-list">
            <li>Válasszon világos, barátságos képeket mosolygó emberekkel</li>
            <li>Kerülje a sötét vagy zsúfolt képeket</li>
            <li>A széles (fekvő) képek működnek legjobban háttérként</li>
            <li>Időnként frissítse a képeket a változatosság kedvéért</li>
            <li>Véletlenszerű módban a nem tetsző képeket cserélheti ki a × gombbal</li>
          </ul>
          <p className="hero-admin-tips-footer">Köszönet a gondoskodásért! ♥</p>
        </aside>
      </div>
    </div>
  );
};

export default HomepageHeroManager;
